import React, { useState, useEffect } from 'react';
import { api } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useIsMobile } from '../lib/responsive';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Building2, MapPin, Users, Plus, Trash2, Shield, ScrollText, Lock,
  ShieldCheck, ShieldOff, Search, UserPlus, Mail, Landmark, CreditCard, Tag,
  Store, ChefHat, IndianRupee, Fingerprint, Loader2, Monitor, AlertTriangle, Copy
} from 'lucide-react';
import {
  isWebAuthnSupported, isPlatformAuthenticatorAvailable,
  registerCredential, storeEmailForBiometric, clearStoredEmail
} from '../lib/webauthn';

export default function Settings() {
  const { user } = useAuth();
  const [ventures, setVentures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [newCatLabel, setNewCatLabel] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addVentureOpen, setAddVentureOpen] = useState(false);
  const [addLocationOpen, setAddLocationOpen] = useState(false);
  const [addBankAccountOpen, setAddBankAccountOpen] = useState(false);
  const [inviteUserOpen, setInviteUserOpen] = useState(false);
  const [twoFAStatus, setTwoFAStatus] = useState(false);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [auditSearch, setAuditSearch] = useState('');
  const [vendors, setVendors] = useState([]);
  const [newVendor, setNewVendor] = useState('');
  const [ingredientLib, setIngredientLib] = useState([]);
  const [newIngredient, setNewIngredient] = useState({ name: '', unit: 'kg', price: '' });
  const [duplicates, setDuplicates] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [selectedDuplicates, setSelectedDuplicates] = useState([]);
  
  // Face ID / Touch ID state
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const isMobile = useIsMobile();

  // Bank account form
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    account_number: '',
    account_type: 'savings',
    venture_id: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [venturesRes, locationsRes, usersRes, bankRes, catsRes, vendorsRes, ingLibRes] = await Promise.all([
        api.get('/ventures'), 
        api.get('/locations'), 
        api.get('/users').catch(() => ({ data: [] })),
        api.get('/bank-accounts').catch(() => ({ data: [] })),
        api.get('/categories/custom').catch(() => ({ data: [] })),
        api.get('/library/vendors').catch(() => ({ data: [] })),
        api.get('/library/ingredients').catch(() => ({ data: [] }))
      ]);
      setVentures(venturesRes.data);
      setLocations(locationsRes.data);
      setUsers(usersRes.data);
      setBankAccounts(bankRes.data);
      setCustomCategories(catsRes.data);
      setVendors(vendorsRes.data);
      setIngredientLib(ingLibRes.data);
      try { const auditRes = await api.get('/audit-logs?limit=50'); setAuditLogs(auditRes.data); } catch {}
      try { const twoFARes = await api.get('/auth/2fa/status'); setTwoFAStatus(twoFARes.data.enabled); } catch {}
      // Check biometric support and status
      try {
        const supported = await isPlatformAuthenticatorAvailable();
        console.log('Biometric supported:', supported);
        setBiometricSupported(supported);
        if (supported) {
          const bioRes = await api.get('/auth/webauthn/status');
          setBiometricEnabled(bioRes.data.enabled);
        }
      } catch (bioErr) {
        console.error('Biometric check error:', bioErr);
      }
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const getVentureName = (id) => ventures.find(v => v.venture_id === id)?.name || 'Not linked';

  const handleDeleteVenture = async (ventureId) => {
    if (!window.confirm('Delete this venture?')) return;
    try { await api.delete(`/ventures/${ventureId}`); toast.success('Venture deleted'); fetchData(); }
    catch { toast.error('Failed to delete venture'); }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Delete this location?')) return;
    try { await api.delete(`/locations/${locationId}`); toast.success('Location deleted'); fetchData(); }
    catch { toast.error('Failed to delete location'); }
  };

  const handleAddCategory = async () => {
    if (!newCatLabel.trim()) return;
    try {
      const value = newCatLabel.trim().toLowerCase().replace(/\s+/g, '_');
      await api.post('/categories/custom', { value, label: newCatLabel.trim() });
      toast.success(`Category "${newCatLabel.trim()}" added`);
      setNewCatLabel('');
      const res = await api.get('/categories/custom');
      setCustomCategories(res.data);
    } catch { toast.error('Failed to add category'); }
  };

  const handleDeleteCategory = async (value) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/categories/custom/${value}`);
      toast.success('Category deleted');
      setCustomCategories(prev => prev.filter(c => c.value !== value));
    } catch { toast.error('Failed to delete category'); }
  };

  const handleAddBankAccount = async (e) => {
    e.preventDefault();
    if (!bankForm.bank_name || !bankForm.account_number) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await api.post('/bank-accounts', bankForm);
      toast.success('Bank account added');
      setAddBankAccountOpen(false);
      setBankForm({ bank_name: '', account_number: '', account_type: 'savings', venture_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to add bank account');
    }
  };

  const handleDeleteBankAccount = async (bankAccountId) => {
    if (!window.confirm('Delete this bank account?')) return;
    try {
      await api.delete(`/bank-accounts/${bankAccountId}`);
      toast.success('Bank account deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete bank account');
    }
  };

  const handleUpdateUserRole = async (userId, role) => {
    try { await api.patch(`/users/${userId}/role?role=${role}`); toast.success('Role updated'); fetchData(); }
    catch { toast.error('Failed to update role'); }
  };

  const handleDeleteUser = async (u) => {
    if (u.user_id === user?.user_id) { toast.error("You can't delete yourself"); return; }
    if (!window.confirm(`Remove ${u.name || u.email} from the system?`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success('User removed'); fetchData(); }
    catch { toast.error('Failed to remove user'); }
  };

  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try { const res = await api.post('/auth/2fa/setup'); setTwoFASetup(res.data); }
    catch { toast.error('Failed to setup 2FA'); }
    finally { setTwoFALoading(false); }
  };

  const handleVerify2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) { toast.error('Enter a 6-digit code'); return; }
    setTwoFALoading(true);
    try { await api.post('/auth/2fa/verify', { code: twoFACode }); toast.success('2FA enabled!'); setTwoFAStatus(true); setTwoFASetup(null); setTwoFACode(''); }
    catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setTwoFALoading(false); }
  };

  const handleDisable2FA = async () => {
    if (!twoFACode || twoFACode.length !== 6) { toast.error('Enter your current 2FA code'); return; }
    setTwoFALoading(true);
    try { await api.post('/auth/2fa/disable', { code: twoFACode }); toast.success('2FA disabled'); setTwoFAStatus(false); setTwoFACode(''); }
    catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setTwoFALoading(false); }
  };

  // Vendor Library handlers
  const handleAddVendor = async () => {
    if (!newVendor.trim()) return;
    try {
      await api.post('/library/vendors', { name: newVendor.trim() });
      toast.success(`Vendor "${newVendor.trim()}" added`);
      setNewVendor('');
      const res = await api.get('/library/vendors');
      setVendors(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add vendor');
    }
  };

  const handleDeleteVendor = async (vendorName) => {
    if (!window.confirm(`Delete vendor "${vendorName}"?`)) return;
    try {
      await api.delete(`/library/vendors/${encodeURIComponent(vendorName)}`);
      toast.success('Vendor deleted');
      setVendors(prev => prev.filter(v => v.name.toLowerCase() !== vendorName.toLowerCase()));
    } catch { toast.error('Failed to delete vendor'); }
  };

  // Ingredient Library handlers
  const handleAddIngredient = async () => {
    if (!newIngredient.name.trim()) return;
    try {
      await api.post('/library/ingredients', {
        name: newIngredient.name.trim(),
        unit: newIngredient.unit,
        price: newIngredient.price ? parseFloat(newIngredient.price) : null
      });
      toast.success(`Ingredient "${newIngredient.name.trim()}" added`);
      setNewIngredient({ name: '', unit: 'kg', price: '' });
      const res = await api.get('/library/ingredients');
      setIngredientLib(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add ingredient');
    }
  };

  const handleDeleteIngredient = async (ingredientName) => {
    if (!window.confirm(`Delete ingredient "${ingredientName}"?`)) return;
    try {
      await api.delete(`/library/ingredients/${encodeURIComponent(ingredientName)}`);
      toast.success('Ingredient deleted');
      setIngredientLib(prev => prev.filter(i => i.name.toLowerCase() !== ingredientName.toLowerCase()));
    } catch { toast.error('Failed to delete ingredient'); }
  };

  // Face ID / Touch ID handlers
  const handleEnableBiometric = async () => {
    setBiometricLoading(true);
    try {
      // Get registration options
      const optionsRes = await api.post('/auth/webauthn/register/options');
      const options = optionsRes.data;
      
      // Create credential using Face ID / Touch ID
      const credential = await registerCredential(options);
      
      // Verify with backend
      await api.post('/auth/webauthn/register/verify', credential);
      
      // Store email for quick login
      storeEmailForBiometric(user.email);
      
      setBiometricEnabled(true);
      toast.success('Face ID / Touch ID enabled!');
    } catch (err) {
      console.error('Biometric registration error:', err);
      // More specific error messages
      if (err.name === 'NotAllowedError') {
        toast.error('Biometric authentication was cancelled or not allowed');
      } else if (err.name === 'NotSupportedError') {
        toast.error('Biometric login not supported on this device');
      } else if (err.name === 'SecurityError') {
        toast.error('Security error - please ensure you are on HTTPS');
      } else {
        toast.error(err?.response?.data?.detail || err?.message || 'Failed to enable biometric login');
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  // Duplicate detection handlers
  const [dupSummary, setDupSummary] = useState({ expense: 0, revenue: 0 });

  const detectDuplicates = async () => {
    setDuplicatesLoading(true);
    setSelectedDuplicates([]);
    try {
      const res = await api.get('/expenses/detect-duplicates');
      setDuplicates(res.data.duplicates || []);
      setDupSummary({
        expense: res.data.expense_duplicate_count || 0,
        revenue: res.data.revenue_duplicate_count || 0
      });
      if (res.data.duplicate_count === 0) {
        toast.success('No duplicates found!');
      } else {
        toast.info(`Found ${res.data.expense_duplicate_count} expense & ${res.data.revenue_duplicate_count} revenue duplicate pairs`);
      }
    } catch (err) {
      toast.error('Failed to detect duplicates');
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const handleDeleteDuplicates = async () => {
    if (selectedDuplicates.length === 0) {
      toast.error('Select duplicates to delete');
      return;
    }
    if (!window.confirm(`Delete ${selectedDuplicates.length} duplicate items? This cannot be undone.`)) return;
    try {
      // Split selected IDs into expense_ids and revenue_ids
      const expenseIds = selectedDuplicates.filter(s => s.startsWith('exp_')).map(s => s);
      const revenueIds = selectedDuplicates.filter(s => s.startsWith('rev_')).map(s => s);
      await api.post('/expenses/delete-duplicates', { expense_ids: expenseIds, revenue_ids: revenueIds });
      toast.success(`Deleted ${selectedDuplicates.length} duplicates`);
      setSelectedDuplicates([]);
      detectDuplicates();
    } catch (err) {
      toast.error('Failed to delete duplicates');
    }
  };

  const toggleDuplicateSelection = (id) => {
    setSelectedDuplicates(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAllDuplicates = () => {
    // Select item_2 from each duplicate pair (the one we recommend deleting)
    const allItem2Ids = duplicates.map(d => d.item_2?.id).filter(Boolean);
    setSelectedDuplicates(prev => prev.length === allItem2Ids.length ? [] : allItem2Ids);
  };

  const handleDisableBiometric = async () => {
    if (!window.confirm('Disable Face ID / Touch ID login?')) return;
    setBiometricLoading(true);
    try {
      await api.delete('/auth/webauthn/disable');
      clearStoredEmail();
      setBiometricEnabled(false);
      toast.success('Face ID / Touch ID disabled');
    } catch {
      toast.error('Failed to disable biometric login');
    } finally {
      setBiometricLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">Settings</h1>
        <p className="text-zinc-500 mt-1">Manage ventures, locations, and user access</p>
      </div>

      <Tabs defaultValue="ventures" className="space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 pb-2">
          <TabsList className="inline-flex min-w-max">
            <TabsTrigger value="ventures" className="flex items-center gap-2"><Building2 className="w-4 h-4" /><span className="hidden sm:inline">Ventures</span><span className="sm:hidden">Ven</span></TabsTrigger>
            <TabsTrigger value="locations" className="flex items-center gap-2"><MapPin className="w-4 h-4" /><span className="hidden sm:inline">Locations</span><span className="sm:hidden">Loc</span></TabsTrigger>
            {!isMobile && <TabsTrigger value="bank-accounts" className="flex items-center gap-2"><Landmark className="w-4 h-4" />Bank Accounts</TabsTrigger>}
            <TabsTrigger value="categories" className="flex items-center gap-2"><Tag className="w-4 h-4" /><span className="hidden sm:inline">Categories</span><span className="sm:hidden">Cat</span></TabsTrigger>
            {!isMobile && <TabsTrigger value="users" className="flex items-center gap-2"><Users className="w-4 h-4" />Users</TabsTrigger>}
            {!isMobile && <TabsTrigger value="vendors" className="flex items-center gap-2"><Store className="w-4 h-4" />Vendors</TabsTrigger>}
            {!isMobile && <TabsTrigger value="ingredients-lib" className="flex items-center gap-2"><ChefHat className="w-4 h-4" />Ingredients</TabsTrigger>}
            {!isMobile && <TabsTrigger value="audit" className="flex items-center gap-2"><ScrollText className="w-4 h-4" />Audit Log</TabsTrigger>}
            {!isMobile && <TabsTrigger value="duplicates" className="flex items-center gap-2"><Copy className="w-4 h-4" />Duplicates</TabsTrigger>}
            <TabsTrigger value="security" className="flex items-center gap-2"><Lock className="w-4 h-4" /><span className="hidden sm:inline">Security</span><span className="sm:hidden">Sec</span></TabsTrigger>
          </TabsList>
        </div>

        {/* Mobile notice for hidden tabs */}
        {isMobile && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
            <Monitor className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-700">Some settings (Bank Accounts, Users, Library) are available on desktop only.</p>
          </div>
        )}

        {/* Ventures Tab */}
        <TabsContent value="ventures">
          <Card className="roch-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium">Ventures</CardTitle>
                <Button onClick={() => setAddVentureOpen(true)} size="sm" className="rounded-xl" data-testid="add-venture-btn"><Plus className="w-4 h-4 mr-1" />Add Venture</Button>
              </div>
            </CardHeader>
            <CardContent>
              {ventures.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center mx-auto mb-3"><Building2 className="w-5 h-5 text-zinc-400" /></div>
                  <p className="text-sm text-zinc-500 mb-4">No ventures created yet</p>
                  <Button onClick={() => setAddVentureOpen(true)} size="sm">Create First Venture</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {ventures.map((venture) => (
                    <div key={venture.venture_id} className="flex items-center justify-between p-4 border border-zinc-100 rounded-xl">
                      <div>
                        <h3 className="font-medium">{venture.name}</h3>
                        {venture.description && <p className="text-sm text-zinc-500">{venture.description}</p>}
                        <p className="text-xs text-zinc-400 mt-1">{locations.filter(l => l.venture_id === venture.venture_id).length} location(s)</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteVenture(venture.venture_id)} className="text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-venture-${venture.venture_id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations">
          <Card className="roch-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium">Locations</CardTitle>
                <Button onClick={() => setAddLocationOpen(true)} size="sm" className="rounded-xl" disabled={ventures.length === 0} data-testid="add-location-btn"><Plus className="w-4 h-4 mr-1" />Add Location</Button>
              </div>
            </CardHeader>
            <CardContent>
              {ventures.length === 0 ? (
                <div className="text-center py-8"><p className="text-sm text-zinc-500">Create a venture first</p></div>
              ) : locations.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center mx-auto mb-3"><MapPin className="w-5 h-5 text-zinc-400" /></div>
                  <p className="text-sm text-zinc-500 mb-4">No locations created yet</p>
                  <Button onClick={() => setAddLocationOpen(true)} size="sm">Create First Location</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {locations.map((location) => (
                    <div key={location.location_id} className="flex items-center justify-between p-4 border border-zinc-100 rounded-xl">
                      <div>
                        <h3 className="font-medium">{location.name}</h3>
                        <p className="text-sm text-zinc-500">{getVentureName(location.venture_id)}</p>
                        {location.address && <p className="text-xs text-zinc-400 mt-1">{location.address}</p>}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteLocation(location.location_id)} className="text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-location-${location.location_id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bank Accounts Tab */}
        <TabsContent value="bank-accounts">
          <Card className="roch-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <Landmark className="w-4 h-4" />
                  Bank Accounts
                  <span className="text-xs font-normal text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Shield className="w-3 h-3" />AES-256 Encrypted
                  </span>
                </CardTitle>
                <Button onClick={() => setAddBankAccountOpen(true)} size="sm" className="rounded-xl" data-testid="add-bank-account-btn">
                  <Plus className="w-4 h-4 mr-1" />Add Account
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {bankAccounts.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Landmark className="w-5 h-5 text-zinc-400" />
                  </div>
                  <p className="text-sm text-zinc-500 mb-4">No bank accounts added yet</p>
                  <Button onClick={() => setAddBankAccountOpen(true)} size="sm">Add First Account</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {bankAccounts.map((account) => (
                    <div key={account.bank_account_id} className="p-4 border border-zinc-100 rounded-xl">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-medium">{account.bank_name}{account.label ? ` - ${account.label}` : ''}</h3>
                            {account.account_number_masked && <p className="text-sm text-zinc-500 font-mono">{account.account_number_masked}</p>}
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${account.account_type === 'current' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                {account.account_type === 'current' ? 'Current' : 'Savings'}
                              </span>
                              {account.venture_id && (
                                <span className="text-xs text-zinc-400">
                                  {getVentureName(account.venture_id)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteBankAccount(account.bank_account_id)} 
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-bank-account-${account.bank_account_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {/* Transaction Stats */}
                      {account.stats && account.stats.total_transactions > 0 && (
                        <div className="mt-3 pt-3 border-t border-zinc-100">
                          <div className="grid grid-cols-4 gap-3 text-center">
                            <div>
                              <p className="text-xs text-zinc-400">Expenses</p>
                              <p className="text-sm font-medium">{account.stats.expense_count}</p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-400">Revenue</p>
                              <p className="text-sm font-medium">{account.stats.revenue_count}</p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-400">Transfers</p>
                              <p className="text-sm font-medium">{account.stats.transfer_count}</p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-400">Total Linked</p>
                              <p className="text-sm font-semibold text-blue-600">{account.stats.total_transactions}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {account.stats && account.stats.total_transactions === 0 && (
                        <div className="mt-3 pt-3 border-t border-zinc-100">
                          <p className="text-xs text-zinc-400 text-center">No linked transactions yet. Import a bank statement to link transactions.</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card className="roch-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-medium flex items-center gap-2"><Tag className="w-4 h-4" />Expense Categories</CardTitle>
                  <p className="text-xs text-zinc-500 mt-1">Manage categories used for transaction classification</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-6">
                <Input placeholder="New category name (e.g. Food, Insurance)" value={newCatLabel} onChange={(e) => setNewCatLabel(e.target.value)} className="flex-1" data-testid="new-category-input" onKeyDown={(e) => { if (e.key === 'Enter' && newCatLabel.trim()) { handleAddCategory(); } }} />
                <Button onClick={handleAddCategory} disabled={!newCatLabel.trim()} data-testid="add-category-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Custom Categories</h3>
                  {customCategories.length === 0 ? (
                    <p className="text-sm text-zinc-400 py-3">No custom categories yet. Add one above.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {customCategories.map((cat) => (
                        <div key={cat.value} className="flex items-center justify-between bg-white border border-zinc-200 rounded-lg px-3 py-2 group hover:border-zinc-300 transition-colors">
                          <span className="text-sm font-medium text-zinc-800">{cat.label}</span>
                          <button onClick={() => handleDeleteCategory(cat.value)} className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-red-500" data-testid={`delete-cat-${cat.value}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Built-in Categories</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      { value: 'ingredients', label: 'Ingredients' }, { value: 'packaging', label: 'Packaging' },
                      { value: 'kitchen_supplies', label: 'Kitchen Supplies' }, { value: 'salaries', label: 'Salaries' },
                      { value: 'freelancers', label: 'Freelancers' }, { value: 'rent', label: 'Rent' },
                      { value: 'utilities', label: 'Utilities' }, { value: 'marketing', label: 'Marketing' },
                      { value: 'influencers', label: 'Influencers/Content' }, { value: 'equipment', label: 'Equipment' },
                      { value: 'repairs', label: 'Repairs/Maintenance' }, { value: 'software', label: 'Software/Subscriptions' },
                      { value: 'transportation', label: 'Transportation/Delivery' }, { value: 'owner_withdrawal', label: 'Owner Withdrawal' },
                      { value: 'credit_card_settlement', label: 'Credit Card Settlement' },
                      { value: 'investment', label: 'Investment' }, { value: 'loan_emi', label: 'Loan EMI' },
                      { value: 'revenue', label: 'Revenue' }, { value: 'other', label: 'Other' }
                    ].map((cat) => (
                      <div key={cat.value} className="flex items-center bg-zinc-50 border border-zinc-100 rounded-lg px-3 py-2">
                        <span className="text-sm text-zinc-600">{cat.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="roch-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium flex items-center gap-2"><Shield className="w-4 h-4" />User Management</CardTitle>
                <div className="flex items-center gap-3">
                  <div className="relative w-56">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input placeholder="Search users..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="pl-9 h-9" data-testid="users-search" />
                  </div>
                  <Button onClick={() => setInviteUserOpen(true)} size="sm" className="rounded-xl" data-testid="invite-user-btn"><UserPlus className="w-4 h-4 mr-1" />Add User</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <div className="text-center py-8"><p className="text-sm text-zinc-500">No users found</p></div>
              ) : (
                <div className="space-y-3">
                  {users.filter(u => !userSearch || u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase())).map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between p-4 border border-zinc-100 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-zinc-100 rounded-full flex items-center justify-center">
                          {u.picture ? <img src={u.picture} alt="" className="w-10 h-10 rounded-full" /> : <span className="text-sm font-medium text-zinc-600">{u.name?.charAt(0) || 'U'}</span>}
                        </div>
                        <div>
                          <h3 className="font-medium">{u.name}</h3>
                          <p className="text-sm text-zinc-500">{u.email}</p>
                          {u.invited && !u.picture && <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Invited - awaiting login</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={u.role} onValueChange={(value) => handleUpdateUserRole(u.user_id, value)} disabled={u.user_id === user?.user_id}>
                          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="staff">Staff</SelectItem>
                            <SelectItem value="accountant">Accountant</SelectItem>
                          </SelectContent>
                        </Select>
                        {u.user_id !== user?.user_id && (
                          <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteUser(u)} data-testid={`delete-user-${u.user_id}`}><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-6 p-4 bg-zinc-50 rounded-xl">
                <h4 className="font-medium text-sm mb-2">Role Permissions</h4>
                <div className="space-y-2 text-xs text-zinc-600">
                  <p><strong>Owner:</strong> Full system access, manage users, view all data</p>
                  <p><strong>Staff:</strong> Add expenses, revenues, ingredients (assigned locations only)</p>
                  <p><strong>Accountant:</strong> View-only access, export reports for tax filing</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendors Library Tab */}
        <TabsContent value="vendors">
          <Card className="roch-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-medium flex items-center gap-2"><Store className="w-4 h-4" />Vendor Library</CardTitle>
                  <p className="text-xs text-zinc-500 mt-1">Manage your vendor database for auto-suggestions</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-6">
                <Input 
                  placeholder="New vendor name (e.g., Metro Cash & Carry)" 
                  value={newVendor} 
                  onChange={(e) => setNewVendor(e.target.value)} 
                  className="flex-1" 
                  data-testid="new-vendor-input"
                  onKeyDown={(e) => { if (e.key === 'Enter' && newVendor.trim()) { handleAddVendor(); } }}
                />
                <Button onClick={handleAddVendor} disabled={!newVendor.trim()} data-testid="add-vendor-btn">
                  <Plus className="w-4 h-4 mr-1" />Add
                </Button>
              </div>

              {vendors.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Store className="w-5 h-5 text-zinc-400" />
                  </div>
                  <p className="text-sm text-zinc-500 mb-2">No vendors in library yet</p>
                  <p className="text-xs text-zinc-400">Vendors are auto-added when you import statements</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs text-zinc-500">{vendors.length} vendors in your library</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[400px] overflow-y-auto">
                    {vendors.map((v) => (
                      <div 
                        key={v.name_lower || v.name} 
                        className="flex items-center justify-between bg-white border border-zinc-200 rounded-lg px-3 py-2 group hover:border-zinc-300 transition-colors"
                      >
                        <span className="text-sm font-medium text-zinc-800 truncate">{v.name}</span>
                        <button 
                          onClick={() => handleDeleteVendor(v.name)} 
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-red-500 ml-2 flex-shrink-0"
                          data-testid={`delete-vendor-${v.name_lower || v.name}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ingredients Library Tab */}
        <TabsContent value="ingredients-lib">
          <Card className="roch-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-medium flex items-center gap-2"><ChefHat className="w-4 h-4" />Ingredient Library</CardTitle>
                  <p className="text-xs text-zinc-500 mt-1">Manage ingredients with price history for auto-suggestions</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-2 mb-6">
                <Input 
                  placeholder="Ingredient name (e.g., Cream, Sugar)" 
                  value={newIngredient.name} 
                  onChange={(e) => setNewIngredient({ ...newIngredient, name: e.target.value })} 
                  className="flex-1" 
                  data-testid="new-ingredient-name-input"
                />
                <Select value={newIngredient.unit} onValueChange={(v) => setNewIngredient({ ...newIngredient, unit: v })}>
                  <SelectTrigger className="w-24" data-testid="new-ingredient-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="L">L</SelectItem>
                    <SelectItem value="ml">ml</SelectItem>
                    <SelectItem value="pcs">pcs</SelectItem>
                    <SelectItem value="dozen">dozen</SelectItem>
                    <SelectItem value="box">box</SelectItem>
                    <SelectItem value="packet">packet</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative w-28">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
                  <Input 
                    type="number" 
                    placeholder="Price" 
                    value={newIngredient.price} 
                    onChange={(e) => setNewIngredient({ ...newIngredient, price: e.target.value })} 
                    className="pl-8" 
                    data-testid="new-ingredient-price-input"
                  />
                </div>
                <Button 
                  onClick={handleAddIngredient} 
                  disabled={!newIngredient.name.trim()} 
                  data-testid="add-ingredient-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />Add
                </Button>
              </div>

              {ingredientLib.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <ChefHat className="w-5 h-5 text-zinc-400" />
                  </div>
                  <p className="text-sm text-zinc-500 mb-2">No ingredients in library yet</p>
                  <p className="text-xs text-zinc-400">Ingredients are auto-added when you import bills</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs text-zinc-500">{ingredientLib.length} ingredients in your library</p>
                  <div className="roch-table-wrap max-h-[400px] overflow-y-auto">
                    <table className="roch-table">
                      <thead>
                        <tr>
                          <th>Ingredient</th>
                          <th>Unit</th>
                          <th className="text-right">Last Price</th>
                          <th className="w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {ingredientLib.map((ing) => (
                          <tr key={ing.name_lower || ing.name}>
                            <td className="font-medium">{ing.name}</td>
                            <td className="text-zinc-500">{ing.unit || '-'}</td>
                            <td className="text-right text-zinc-600">
                              {ing.last_price ? `₹${ing.last_price.toFixed(2)}/${ing.unit || 'unit'}` : '-'}
                            </td>
                            <td>
                              <button 
                                onClick={() => handleDeleteIngredient(ing.name)} 
                                className="text-zinc-400 hover:text-red-500 p-1"
                                data-testid={`delete-ingredient-${ing.name_lower || ing.name}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit">
          <Card className="roch-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium flex items-center gap-2"><ScrollText className="w-4 h-4" />Activity Log</CardTitle>
                <div className="relative w-56"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input placeholder="Search logs..." value={auditSearch} onChange={(e) => setAuditSearch(e.target.value)} className="pl-9 h-9" data-testid="audit-search" /></div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {auditLogs.length === 0 ? (
                <div className="text-center py-8"><p className="text-sm text-zinc-500">No activity recorded yet</p></div>
              ) : (
                <table className="roch-table" data-testid="audit-logs-table">
                  <thead><tr><th>Time</th><th>User</th><th>Action</th><th>Entity</th></tr></thead>
                  <tbody>
                    {auditLogs.filter(log => !auditSearch || log.action?.toLowerCase().includes(auditSearch.toLowerCase()) || log.user_name?.toLowerCase().includes(auditSearch.toLowerCase()) || log.entity_type?.toLowerCase().includes(auditSearch.toLowerCase())).map((log) => (
                      <tr key={log.audit_id}>
                        <td className="text-xs text-zinc-500 whitespace-nowrap">{new Date(log.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="text-sm">{log.user_name || 'System'}</td>
                        <td><span className="badge badge-default text-xs">{log.action?.replace(/_/g, ' ')}</span></td>
                        <td className="text-xs text-zinc-500">{log.entity_type} / {log.entity_id?.slice(0, 12)}...</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <div className="space-y-6">
            {/* Face ID / Touch ID Card */}
            <Card className="roch-card">
              <CardHeader><CardTitle className="text-base font-medium flex items-center gap-2"><Fingerprint className="w-4 h-4" />Face ID / Touch ID</CardTitle></CardHeader>
              <CardContent>
                {biometricSupported ? (
                  <div className="flex items-center justify-between p-4 border border-zinc-100 rounded-xl">
                    <div className="flex items-center gap-3">
                      {biometricEnabled ? (
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Fingerprint className="w-5 h-5 text-green-600" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center">
                          <Fingerprint className="w-5 h-5 text-zinc-400" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-medium">{biometricEnabled ? 'Biometric Login Enabled' : 'Quick Login Available'}</h3>
                        <p className="text-sm text-zinc-500">
                          {biometricEnabled 
                            ? 'Use Face ID or Touch ID to sign in quickly' 
                            : 'Enable biometric authentication for faster login'}
                        </p>
                      </div>
                    </div>
                    {biometricEnabled ? (
                      <Button variant="outline" onClick={handleDisableBiometric} disabled={biometricLoading}>
                        {biometricLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disable'}
                      </Button>
                    ) : (
                      <Button onClick={handleEnableBiometric} disabled={biometricLoading}>
                        {biometricLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Setting up...</> : 'Enable'}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="p-4 border border-amber-200 bg-amber-50 rounded-xl">
                    <p className="text-sm text-amber-800">
                      Biometric login requires a device with Face ID, Touch ID, or Windows Hello. 
                      Please use Safari on iOS/macOS or Chrome on a supported device.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 2FA Card */}
            <Card className="roch-card">
              <CardHeader><CardTitle className="text-base font-medium flex items-center gap-2"><Lock className="w-4 h-4" />Two-Factor Authentication</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 border border-zinc-100 rounded-xl">
                    <div className="flex items-center gap-3">
                      {twoFAStatus ? <ShieldCheck className="w-8 h-8 text-green-600" /> : <ShieldOff className="w-8 h-8 text-zinc-400" />}
                      <div>
                        <h3 className="font-medium">{twoFAStatus ? '2FA is Active' : '2FA is Disabled'}</h3>
                        <p className="text-sm text-zinc-500">{twoFAStatus ? 'Protected with authenticator app' : 'Add extra security'}</p>
                      </div>
                    </div>
                    {!twoFAStatus && !twoFASetup && <Button onClick={handleSetup2FA} disabled={twoFALoading}>{twoFALoading ? 'Setting up...' : 'Enable 2FA'}</Button>}
                  </div>
                  {twoFASetup && !twoFAStatus && (
                    <div className="p-6 border border-amber-200 bg-amber-50/50 rounded-xl space-y-4">
                      <h4 className="font-medium">Scan QR Code</h4>
                      <p className="text-sm text-zinc-600">Scan with your authenticator app (Google Authenticator, Authy, etc.)</p>
                      <div className="flex justify-center py-4"><img src={twoFASetup.qr_code} alt="2FA QR" className="w-48 h-48 rounded-lg" /></div>
                      <div className="p-3 bg-white rounded-lg border"><p className="text-xs text-zinc-500 mb-1">Manual key:</p><code className="text-sm font-mono break-all">{twoFASetup.secret}</code></div>
                      <div><Label>Enter 6-digit code</Label>
                        <div className="flex gap-2 mt-1.5">
                          <Input value={twoFACode} onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} className="text-center text-lg tracking-widest font-mono" />
                          <Button onClick={handleVerify2FA} disabled={twoFALoading || twoFACode.length !== 6}>{twoFALoading ? 'Verifying...' : 'Verify'}</Button>
                        </div>
                      </div>
                    </div>
                  )}
                  {twoFAStatus && (
                    <div className="p-4 border border-zinc-200 rounded-xl space-y-3">
                      <h4 className="font-medium text-sm">Disable 2FA</h4>
                      <p className="text-xs text-zinc-500">Enter current 2FA code to disable</p>
                      <div className="flex gap-2">
                        <Input value={twoFACode} onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} className="text-center tracking-widest font-mono max-w-[200px]" />
                        <Button variant="destructive" onClick={handleDisable2FA} disabled={twoFALoading || twoFACode.length !== 6}>Disable</Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Duplicates Tab */}
        <TabsContent value="duplicates">
          <Card className="roch-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Duplicate Detection
              </CardTitle>
              <div className="flex gap-2">
                {selectedDuplicates.length > 0 && (
                  <Button variant="destructive" onClick={handleDeleteDuplicates} size="sm" data-testid="delete-duplicates-btn">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Selected ({selectedDuplicates.length})
                  </Button>
                )}
                <Button onClick={detectDuplicates} disabled={duplicatesLoading} size="sm" data-testid="scan-duplicates-btn">
                  {duplicatesLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Scan for Duplicates
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {duplicates.length === 0 ? (
                <div className="text-center py-8 text-zinc-500" data-testid="duplicates-empty-state">
                  <Copy className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
                  <p className="text-sm">Click "Scan for Duplicates" to find potential duplicate expenses & revenues</p>
                  <p className="text-xs text-zinc-400 mt-1">Checks for same date + amount + similar vendor/source across both collections</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Summary banner */}
                  <div className="flex flex-wrap gap-3">
                    <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex-1 min-w-[200px]">
                      <p className="text-sm text-amber-800 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span><strong>{duplicates.length}</strong> duplicate pairs found</span>
                      </p>
                    </div>
                    {dupSummary.expense > 0 && (
                      <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700"><strong>{dupSummary.expense}</strong> expense duplicates</p>
                      </div>
                    )}
                    {dupSummary.revenue > 0 && (
                      <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700"><strong>{dupSummary.revenue}</strong> revenue duplicates</p>
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-zinc-500">For each pair, the <strong>second item</strong> (right side) is pre-selected for deletion. Review carefully.</p>

                  {/* Select all */}
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={selectAllDuplicates} data-testid="select-all-duplicates-btn">
                      {selectedDuplicates.length === duplicates.length ? 'Deselect All' : 'Select All (Item 2)'}
                    </Button>
                    <span className="text-xs text-zinc-400">{selectedDuplicates.length} selected</span>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="duplicates-table">
                      <thead className="bg-zinc-50 border-b">
                        <tr>
                          <th className="p-2 w-8"></th>
                          <th className="p-2 text-left text-xs font-medium text-zinc-500">Type</th>
                          <th className="p-2 text-left text-xs font-medium text-zinc-500">Date</th>
                          <th className="p-2 text-right text-xs font-medium text-zinc-500">Amount</th>
                          <th className="p-2 text-left text-xs font-medium text-zinc-500">Item 1</th>
                          <th className="p-2 text-left text-xs font-medium text-zinc-500">Item 2 (to delete)</th>
                          <th className="p-2 text-left text-xs font-medium text-zinc-500">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {duplicates.map((dup, idx) => {
                          const item2Id = dup.item_2?.id;
                          const isSelected = selectedDuplicates.includes(item2Id);
                          return (
                            <tr key={idx} className={`border-b hover:bg-zinc-50 ${isSelected ? 'bg-red-50/50' : ''}`} data-testid={`duplicate-row-${idx}`}>
                              <td className="p-2">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleDuplicateSelection(item2Id)}
                                  className="w-4 h-4 rounded border-zinc-300 text-red-600 focus:ring-red-500"
                                  data-testid={`duplicate-checkbox-${idx}`}
                                />
                              </td>
                              <td className="p-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${dup.type === 'expense' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                  {dup.type === 'expense' ? 'Expense' : 'Revenue'}
                                </span>
                              </td>
                              <td className="p-2 text-zinc-600 whitespace-nowrap">{dup.item_1?.date}</td>
                              <td className={`p-2 text-right font-medium whitespace-nowrap ${dup.type === 'expense' ? 'text-red-600' : 'text-green-600'}`}>
                                {dup.type === 'expense' ? '-' : '+'}₹{dup.item_1?.amount?.toLocaleString('en-IN')}
                              </td>
                              <td className="p-2">
                                <div className="text-zinc-800 font-medium truncate max-w-[160px]" title={dup.item_1?.label}>{dup.item_1?.label}</div>
                                {dup.item_1?.category && <div className="text-xs text-zinc-400">{dup.item_1.category}</div>}
                              </td>
                              <td className="p-2">
                                <div className="text-zinc-800 font-medium truncate max-w-[160px]" title={dup.item_2?.label}>{dup.item_2?.label}</div>
                                {dup.item_2?.category && <div className="text-xs text-zinc-400">{dup.item_2.category}</div>}
                              </td>
                              <td className="p-2 text-xs text-amber-600 max-w-[150px] truncate" title={dup.reason}>{dup.reason}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Data Fix Tool */}
          <Card className="roch-card mt-4">
            <CardHeader>
              <CardTitle className="text-base font-medium">Data Repair Tools</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Fix Expense Verification Status</p>
                  <p className="text-xs text-zinc-500">Marks all bank-imported and manual expenses as verified (one-time fix)</p>
                </div>
                <Button size="sm" variant="outline" data-testid="fix-verification-btn" onClick={async () => {
                  try {
                    const res = await api.post('/admin/fix-expense-verification');
                    toast.success(res.data.message);
                  } catch (err) {
                    toast.error('Failed to fix verification status');
                  }
                }}>Run Fix</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AddVentureDialog open={addVentureOpen} onOpenChange={setAddVentureOpen} onSuccess={fetchData} />
      <AddLocationDialog open={addLocationOpen} onOpenChange={setAddLocationOpen} ventures={ventures} onSuccess={fetchData} />
      <InviteUserDialog open={inviteUserOpen} onOpenChange={setInviteUserOpen} locations={locations} onSuccess={fetchData} />
      
      {/* Add Bank Account Dialog */}
      <Dialog open={addBankAccountOpen} onOpenChange={setAddBankAccountOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5" />
              Add Bank Account
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddBankAccount} className="space-y-4 mt-4">
            <div>
              <Label>Bank Name</Label>
              <Input 
                placeholder="e.g., HDFC Bank, ICICI Bank" 
                value={bankForm.bank_name} 
                onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })} 
                className="mt-1.5" 
                data-testid="bank-name-input"
              />
            </div>
            <div>
              <Label>Account Number</Label>
              <Input 
                placeholder="Enter full account number" 
                value={bankForm.account_number} 
                onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })} 
                className="mt-1.5 font-mono" 
                data-testid="bank-account-number-input"
              />
              <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                <Shield className="w-3 h-3 text-emerald-500" />
                Encrypted with AES-256. Only last 4 digits shown in UI.
              </p>
            </div>
            <div>
              <Label>Account Type</Label>
              <Select value={bankForm.account_type} onValueChange={(v) => setBankForm({ ...bankForm, account_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="bank-account-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="savings">Savings Account</SelectItem>
                  <SelectItem value="current">Current Account</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Link to Venture (optional)</Label>
              <Select value={bankForm.venture_id || 'none'} onValueChange={(v) => setBankForm({ ...bankForm, venture_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="mt-1.5" data-testid="bank-venture-link">
                  <SelectValue placeholder="Select venture" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not linked</SelectItem>
                  {ventures.map((v) => (
                    <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full" data-testid="bank-account-submit">
              Add Bank Account
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddVentureDialog({ open, onOpenChange, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Enter a venture name'); return; }
    setLoading(true);
    try { await api.post('/ventures', form); toast.success('Venture created'); setForm({ name: '', description: '' }); onOpenChange(false); onSuccess(); }
    catch { toast.error('Failed to create venture'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]"><DialogHeader><DialogTitle>Add Venture</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label>Venture Name</Label><Input placeholder="e.g., ROCH Cake Studio" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Description (optional)</Label><Input placeholder="Brief description..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
          <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Creating...' : 'Create Venture'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddLocationDialog({ open, onOpenChange, ventures, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', venture_id: '', address: '' });
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.venture_id) { toast.error('Fill all required fields'); return; }
    setLoading(true);
    try { await api.post('/locations', form); toast.success('Location created'); setForm({ name: '', venture_id: '', address: '' }); onOpenChange(false); onSuccess(); }
    catch { toast.error('Failed to create location'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]"><DialogHeader><DialogTitle>Add Location</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div><Label>Location Name</Label><Input placeholder="e.g., Central Kitchen" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Venture</Label><Select value={form.venture_id} onValueChange={(v) => setForm({ ...form, venture_id: v })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select venture" /></SelectTrigger><SelectContent>{ventures.map((v) => <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Address (optional)</Label><Input placeholder="Location address..." value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" /></div>
          <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Creating...' : 'Create Location'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InviteUserDialog({ open, onOpenChange, locations, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'staff' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Enter an email address'); return; }
    setLoading(true);
    try {
      await api.post(`/users/invite?email=${encodeURIComponent(form.email)}&role=${form.role}&name=${encodeURIComponent(form.name)}`);
      toast.success(`User invited! They can now login with Google using ${form.email}`);
      setForm({ email: '', name: '', role: 'staff' });
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to invite user');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]" data-testid="invite-user-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" />Add New User</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <Mail className="w-4 h-4 inline mr-1" />
            The invited user can login using their <strong>Google account</strong> with the email you enter below.
          </div>
          <div><Label>Email Address</Label><Input type="email" placeholder="user@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="invite-email" /></div>
          <div><Label>Name (optional)</Label><Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Owner - Full access</SelectItem>
                <SelectItem value="staff">Staff - Data entry for assigned locations</SelectItem>
                <SelectItem value="accountant">Accountant - View-only, export reports</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="invite-submit">{loading ? 'Inviting...' : 'Add User'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
